import Button from "react-bootstrap/Button";
import { useEffect, useRef, useState } from "react";
import { useLazyGetGenresQuery } from "../../api/store/genres";
import {
    genreSelectStyles,
    airDayOptions,
    yearNameOptions,
    airDaySelectStyles,
    seasonSelectStyles,
} from "./variables";
import Select from "react-select";
import {
    useCreateAnimeMutation,
    useUpdateAnimeMutation,
} from "../../api/store/anime";
import LoadingBars from "../../components/loadingBars";
import { useDispatch } from "react-redux";
import { listedAnimeApi } from "../../api/store/listed-anime";
import { seasonsApi } from "../../api/store/seasons";
import { Editor } from "@tinymce/tinymce-react";
import stringUtil from "../../utils/stringUtil";
import AnimeItem from "../../models/AnimeItem";
import { DateTime } from "luxon";

type Props = {
    onClose: () => void;
    anime?: AnimeItem;
    type?: "Update" | "New";
    onUpdateCompleted?: () => void;
};

const emptyAnime: AnimeItem = {
    id: 0,
    title: "",
    englishTitle: "",
    description: "",
    episodes: 0,
    image: "",

    uniqID: null,

    airDay: null,
    airTime: null,

    startedAiringAt: null,
    finishedAiringAt: null,

    startedAiringAtForm: null,
    finishedAiringAtForm: null,

    hiatusStartedAt: null,
    hiatusEndedAt: null,

    hiatusStartedAtForForm: "",
    hiatusEndedAtForForm: "",

    seasonNumber: null,

    genreString: "",

    prequel: "",
    sequel: "",

    prequelUniqueID: null,
    sequelUniqueID: null,

    movies: [],
    genres: [],

    season: {
        name: "",
        year: 0,
        period: "",
        slug: "",
        animeCount: 0,
        animeItems: []
    },

    characters: [],
    animeLists: [],
    studios: [],
};

const AnimeForm = ({
   onClose,
   anime = emptyAnime,
   type = "Update",
   onUpdateCompleted = () => {},
}: Props) => {
    const editorRef = useRef<any>(null);

    let genreArray: string[] = [];

    if (anime.genreString.length > 0) {
        genreArray = anime.genreString.split(", ").map((genre) => {
            return genre.toLowerCase();
        });
    }

    const clonedAnime = structuredClone(anime);

    clonedAnime.episodes = clonedAnime.episodes ?? 0;

    clonedAnime.startedAiringAtForm = clonedAnime.startedAiringAt
        ? DateTime.fromISO(clonedAnime.startedAiringAt).toFormat("yyyy-MM-dd")
        : "";

    clonedAnime.finishedAiringAtForm = clonedAnime.finishedAiringAt
        ? DateTime.fromISO(clonedAnime.finishedAiringAt).toFormat("yyyy-MM-dd")
        : "";

    if (type !== "New") {
        clonedAnime.season =
            anime.season === null
                ? emptyAnime.season
                : anime.season;
    } else {
        clonedAnime.season = emptyAnime.season;
    }

    const [requestData, setRequestData] = useState<any>({
        ...clonedAnime,
        genres: genreArray.join("|"),
    });
    const [getGenres, { data: genres, isLoading: isGenresLoading }] =
        useLazyGetGenresQuery();
    const [genreOptions, setGenreOptions] = useState([]);
    const [updateAnime, { isLoading: isUpdating }] = useUpdateAnimeMutation();
    const [createAnime] = useCreateAnimeMutation();
    const dispatch = useDispatch();

    const selectedGenreOptions = genreArray.map((animeGenre) => {
        return {
            value: stringUtil.capFirstChar(animeGenre),
            label: stringUtil.capFirstChar(animeGenre),
        };
    });

    useEffect(() => {
        getGenres({});
        if (!isGenresLoading && genres) {
            setGenreOptions(
                genres.map((genre: any) => ({
                    label: stringUtil.capFirstChar(genre.name),
                    value: genre.name.toLowerCase(),
                }))
            );
        }
    }, [genres, getGenres, isGenresLoading]);

    const addFormValueToRequestData = (key: string, value: any, type = "") => {
        setRequestData((prevRequestData: any) => {
            if (type === "Update" && !Object.keys(prevRequestData).includes(key)) {
                console.error("Tried adding a non-existing key to the request data");
                return prevRequestData;
            }

            if (type === "number") {
                value = parseInt(value);
            }

            prevRequestData[key] = value;

            return prevRequestData;
        });
    };

    const doRequest = () => {
        requestData.description = editorRef.current.getContent();
        requestData.startedAiringAt =
            requestData.startedAiringAtForm !== ""
                ? DateTime.fromISO(requestData.startedAiringAtForm).toFormat(
                    "yyyy-MM-dd'T'00:00:00"
                )
                : null;

        requestData.finishedAiringAt =
            requestData.finishedAiringAtForm !== ""
                ? DateTime.fromISO(requestData.finishedAiringAtForm).toFormat(
                    "yyyy-MM-dd'T'00:00:00"
                )
                : null;

        if (type === "Update") {
            updateAnime({
                animeId: requestData.id,
                ...requestData,
            }).then(() => {
                dispatch(listedAnimeApi.util.invalidateTags(["ListedAnime"]));
                dispatch(seasonsApi.util.invalidateTags(["season"]));
                onUpdateCompleted();
                onClose();
            });
        } else {
            createAnime(requestData).then(() => {
                onClose();
            });
        }
    };

    return (
        <form>
            <h3>
                {type} anime
                <Button
                    variant="danger"
                    style={{ float: "right" }}
                    onClick={onClose}
                    disabled={isUpdating}
                >
                    <i className="fas fa-times"></i>
                </Button>
            </h3>
            <div className="row">
                <div className="col-12 col-lg-2">
                    <label htmlFor="title">Title</label>
                </div>
                <div className="col-12 col-lg-10">
                    <input
                        type="text"
                        defaultValue={requestData.title}
                        name="title"
                        id="title"
                        className="form-control"
                        onChange={(ev) => {
                            addFormValueToRequestData("title", ev.target.value, "text");
                        }}
                        disabled={isUpdating}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-12 col-lg-2">
                    <label htmlFor="englishTitle">English title</label>
                </div>
                <div className="col-12 col-lg-10">
                    <input
                        type="text"
                        name="englishTitle"
                        id="englishTitle"
                        className="form-control"
                        defaultValue={requestData.englishTitle}
                        onChange={(ev) => {
                            addFormValueToRequestData("englishTitle", ev.target.value, "text");
                        }}
                        disabled={isUpdating}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-12 col-lg-2">
                    <label>Genres</label>
                </div>
                <div className="col-12 col-lg-10">
                    <Select
                        styles={genreSelectStyles}
                        options={genreOptions}
                        isMulti
                        defaultValue={selectedGenreOptions}
                        onChange={(changes) => {
                            const genreValues = changes.map((genreOption) => {
                                return stringUtil.capFirstChar(genreOption.value);
                            });

                            addFormValueToRequestData(
                                "genreString",
                                genreValues.join(", "),
                                "text"
                            );
                        }}
                        isDisabled={isUpdating}
                    />
                </div>
            </div>

            <div className="row">
                <div className="col-6 col-lg-2 gy-1">
                    <label htmlFor="airDay">Air day</label>
                </div>
                <div className="col-6 col-lg-2 gy-1">
                    <Select
                        styles={airDaySelectStyles}
                        options={airDayOptions}
                        defaultValue={{
                            value: requestData.airDay,
                            label: requestData.airDay,
                        }}
                        onChange={(changes: any) => {
                            addFormValueToRequestData("airDay", changes.value, "text");
                        }}
                        isDisabled={isUpdating}
                    />
                </div>
                <div className="col-6 col-lg-2 gy-1">
                    <label htmlFor="airTime">Air time</label>
                </div>
                <div className="col-6 col-lg-2 gy-1">
                    <input
                        type="time"
                        name="airTime"
                        id="airTime"
                        className="form-control"
                        defaultValue={requestData.airTime}
                        onChange={(ev) => {
                            addFormValueToRequestData("airTime", ev.target.value, "text");
                        }}
                        disabled={isUpdating}
                    />
                </div>
                <div className="col-6 col-lg-2 gy-1">
                    <label htmlFor="episodes">Episodes</label>
                </div>
                <div className="col-6 col-lg-2 gy-1">
                    <input
                        type="number"
                        name="episodes"
                        id="episodes"
                        className="form-control"
                        defaultValue={requestData.episodes ?? 0}
                        onChange={(ev) => {
                            addFormValueToRequestData("episodes", ev.target.value ?? 0, "number");
                        }}
                        disabled={isUpdating}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-6 col-lg-2 gy-1">
                    <label htmlFor="seasonName">Season name</label>
                </div>
                <div className="col-6 col-lg-2 gy-1">
                    <Select
                        styles={seasonSelectStyles}
                        options={yearNameOptions}
                        defaultValue={{
                            value:
                                type === "Update"
                                    ? requestData.season.period.toLowerCase()
                                    : yearNameOptions[0].value,
                            label:
                                type === "Update"
                                    ? stringUtil.capFirstChar(requestData.season.period)
                                    : yearNameOptions[0].label,
                        }}
                        onChange={(changes: any) => {
                            addFormValueToRequestData("seasonName", changes.value, "text");
                        }}
                        isDisabled={isUpdating}
                    />
                </div>
                <div className="col-6 col-lg-2 gy-1">
                    <label htmlFor="seasonYear">Season year</label>
                </div>
                <div className="col-6 col-lg-2 gy-1">
                    <input
                        type="number"
                        id="seasonYear"
                        className="form-control"
                        value={requestData.season.year}
                        onChange={(ev) => {
                            addFormValueToRequestData("seasonYear", ev.target.value, "number");
                        }}
                    />
                </div>
                <div className="col-6 col-lg-2 gy-1">
                    <label htmlFor="seasonNumber">Season number</label>
                </div>
                <div className="col-6 col-lg-2 gy-1">
                    <input
                        type="number"
                        name="seasonNumber"
                        id="seasonNumber"
                        className="form-control"
                        defaultValue={requestData.seasonNumber}
                        onChange={(ev) => {
                            addFormValueToRequestData(
                                "seasonNumber",
                                ev.target.value,
                                "number"
                            );
                        }}
                        disabled={isUpdating}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-6 col-lg-2 gy-1">
                    <label htmlFor="prequel">Prequel (MAL id)</label>
                </div>
                <div className="col-6 col-lg-2 gy-1">
                    <input
                        type="text"
                        name="prequel"
                        id="prequel"
                        className="form-control"
                        defaultValue={requestData.prequelUniqueID}
                        onChange={(ev) => {
                            addFormValueToRequestData(
                                "prequelUniqueID",
                                ev.target.value,
                                "text"
                            );
                        }}
                        disabled={isUpdating}
                    />
                </div>
                <div className="col-6 col-lg-2 gy-1">
                    <label htmlFor="sequel">Sequel (MAL id)</label>
                </div>
                <div className="col-6 col-lg-2 gy-1">
                    <input
                        type="text"
                        name="sequel"
                        id="sequel"
                        className="form-control"
                        defaultValue={requestData.sequelUniqueID}
                        onChange={(ev) => {
                            addFormValueToRequestData(
                                "sequelUniqueID",
                                ev.target.value,
                                "text"
                            );
                        }}
                        disabled={isUpdating}
                    />
                </div>
                <div className="col-12 col-lg-2 gy-1">
                    <label htmlFor="movie">Movies</label>
                </div>
                <div className="col-12 col-lg-2 gy-1">
                    <input
                        type="text"
                        name="movie"
                        id="movie"
                        className="form-control"
                        defaultValue={requestData.movies}
                        onChange={(ev) => {
                            addFormValueToRequestData("movies", ev.target.value, "text");
                        }}
                        disabled={isUpdating}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-6 col-lg-2 gy-1">
                    <label htmlFor="startedAiringAt">Started airing at</label>
                </div>
                <div className="col-6 col-lg-4 gy-1">
                    <input
                        type="date"
                        name="startedAiringAt"
                        id="startedAiringAt"
                        className="form-control"
                        defaultValue={requestData.startedAiringAtForm}
                        onChange={(ev) => {
                            addFormValueToRequestData(
                                "startedAiringAtForm",
                                ev.target.value,
                                "text"
                            );
                        }}
                        disabled={isUpdating}
                    />
                </div>
                <div className="col-6 col-lg-2 gy-1">
                    <label htmlFor="finishedAiringAt">Finished airing at</label>
                </div>
                <div className="col-6 col-lg-4 gy-1">
                    <input
                        type="date"
                        name="finishedAiringAt"
                        id="finishedAiringAt"
                        className="form-control"
                        defaultValue={requestData.finishedAiringAtForm}
                        onChange={(ev) => {
                            addFormValueToRequestData(
                                "finishedAiringAtForm",
                                ev.target.value,
                                "text"
                            );
                        }}
                        disabled={isUpdating}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-6 col-lg-2 gy-2">
                    <label htmlFor="hiatusStartedAt">Hiatus start</label>
                </div>
                <div className="col-6 col-lg-4 gy-1">
                    <input
                        type="date"
                        name="hiatusStartedAt"
                        id="hiatusStartedAt"
                        className="form-control"
                        defaultValue={requestData.hiatusStartedAt}
                        onChange={(ev) => {
                            addFormValueToRequestData(
                                "hiatusStartedAt",
                                ev.target.value,
                                "text"
                            );
                        }}
                        disabled={isUpdating}
                    />
                </div>
                <div className="col-6 col-lg-2 gy-2">
                    <label htmlFor="hiatusEndedAt">Hiatus end</label>
                </div>
                <div className="col-6 col-lg-4 gy-1">
                    <input
                        type="date"
                        name="hiatusEndedAt"
                        id="hiatusEndedAt"
                        className="form-control"
                        defaultValue={requestData.hiatusEndedAt}
                        onChange={(ev) => {
                            addFormValueToRequestData("hiatusEndedAt", ev.target.value, "text");
                        }}
                        disabled={isUpdating}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-12 col-lg-2 gy-2">
                    <label htmlFor="image">Image url</label>
                </div>
                <div className="col-12 col-lg-10 gy-1">
                    <input
                        type="text"
                        name="image"
                        id="image"
                        className="form-control"
                        defaultValue={requestData.image}
                        onChange={(ev) => {
                            addFormValueToRequestData("image", ev.target.value, "text");
                        }}
                        disabled={isUpdating}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-12 col-lg-2 gy-2">
                    <label>Description</label>
                </div>
                <div className="col-12 col-lg-10 gy-1">
                    <Editor
                        apiKey="vpm0w27cpx8clk16dyr7ltfnvwr23g69uomhcact6vygcjrg"
                        onInit={(_evt, editor) => (editorRef.current = editor)}
                        initialValue={anime.description}
                        init={{
                            skin: "oxide-dark",
                            content_css: "dark",
                            height: 300,
                            menubar: false,
                            plugins: ["wordcount"],
                            toolbar:
                                "undo redo | blocks | " +
                                "bold italic alignleft aligncenter " +
                                "alignright alignjustify |",
                            content_style: `body { 
                                font-family:Helvetica,Arial,sans-serif; 
                                font-size:14px; 
                                background-color: #212529 !important
                                }`,
                        }}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-12 col-lg-2 offset-10">
                    {isUpdating && (
                        <LoadingBars style={{ bottom: "10px", right: "20px" }} />
                    )}
                    <button
                        style={{ float: "right", marginTop: "10px" }}
                        className="btn btn-success"
                        onClick={(ev) => {
                            ev.preventDefault();
                            doRequest();
                        }}
                        disabled={isUpdating}
                    >
                        {type === "New" ? `Create` : `Update`}
                    </button>
                </div>
            </div>
        </form>
    );
};

export default AnimeForm;
